import { type ComponentPropsWithoutRef, type ReactNode, Children, isValidElement, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { animated, useSpring } from '@react-spring/web';
import { DragState } from '@use-gesture/react';
import { GridSettings } from './grid-types';
import { GridContext } from './GridContext';
import { GridItemContext } from './GridItemContext';
import { getPositionForIndex, getTargetIndex } from './helpers';
import { swap } from './swap';
import { useMeasure } from './use-measure';
export interface GridDropZoneProps extends ComponentPropsWithoutRef<'div'> {
  boxesPerRow: number;
  rowHeight: number;
  id: string;
  children: ReactNode[];
  disableDrag?: boolean;
  disableDrop?: boolean;
}
interface PlaceholderType {
  startIndex: number;
  targetIndex: number;
}
export function GridDropZone({
  id,
  boxesPerRow,
  children,
  style,
  disableDrag = false,
  disableDrop = false,
  rowHeight,
  ...other
}: GridDropZoneProps) {
  const {
    traverse,
    startTraverse,
    endTraverse,
    register,
    measureAll,
    onChange,
    remove,
    getActiveDropId,
    isRtl
  } = useContext(GridContext);
  const ref = useRef<HTMLDivElement>(null);
  const {
    bounds,
    remeasure
  } = useMeasure(ref);
  const [draggingIndex, setDraggingIndex] = useState<number | null>(null);
  const [placeholder, setPlaceholder] = useState<PlaceholderType | null>(null);
  const traverseIndex = traverse && !traverse.execute && traverse.targetId === id ? traverse.targetIndex : null;
  const grid: GridSettings = useMemo(() => ({
    columnWidth: bounds.width / boxesPerRow,
    boxesPerRow,
    rowHeight
  }), [bounds.width, boxesPerRow, rowHeight]);
  const items = Children.toArray(children).filter(child => isValidElement(child));

  /**
   * Register our dropzone with our grid context
   */

  useEffect(() => {
    register(id, {
      top: bounds.top,
      bottom: bounds.bottom,
      left: bounds.left,
      right: bounds.right,
      width: bounds.width,
      height: bounds.height,
      count: items.length,
      grid,
      disableDrop,
      remeasure
    });
  }, [items.length, disableDrop, bounds, id, grid, register, remeasure]);

  /**
   * Unregister when unmounting
   */

  useEffect(() => () => remove(id), [id, remove]);

  // keep an initial list of our item indexes. We use this
  // when animating swap positions on drag events
  const itemsIndexes = items.map((_, i) => i) ?? [];
  const styles = useSpring({
    height: `${Math.ceil(items.length / boxesPerRow) * rowHeight}px`
  });
  return <animated.div ref={ref} style={{
    ...style,
    position: 'relative',
    ...styles
  }} {...other} data-sentry-element="animated.div" data-sentry-component="GridDropZone" data-sentry-source-file="GridDropZone.tsx">
      {grid.columnWidth === 0 ? null : Children.map(items, (child, i) => {
      const isTraverseTarget = traverse && traverse.targetId === id && traverse.targetIndex === i;
      const order = placeholder ? swap(itemsIndexes, placeholder.startIndex, placeholder.targetIndex) : itemsIndexes;
      const nextIndex = order.indexOf(i);
      const pos = getPositionForIndex(nextIndex, grid, traverseIndex);

      /**
       * Handle a child being dragged
       * @param state
       * @param x
       * @param y
       */

      function onMove(state: DragState, x: number, y: number) {
        if (!ref.current) return;
        if (draggingIndex !== i) {
          setDraggingIndex(i);
        }
        const targetDropId = getActiveDropId(id, x + grid.columnWidth / 2, y + grid.rowHeight / 2);
        if (targetDropId && targetDropId !== id) {
          startTraverse(id, targetDropId, x, y, i);
        } else {
          endTraverse();
        }
        const targetIndex = targetDropId !== id ? items.length : getTargetIndex(i, grid, items.length, (isRtl ? -1 : 1) * state.movement[0], state.movement[1]);
        if (targetIndex !== i) {
          if (placeholder && placeholder.targetIndex !== targetIndex || !placeholder) {
            setPlaceholder({
              targetIndex,
              startIndex: i
            });
          }
        } else if (placeholder) {
          setPlaceholder(null);
        }
      }

      /**
       * Handle drag end events
       */

      function onEnd(state: DragState, x: number, y: number) {
        const targetDropId = getActiveDropId(id, x + grid.columnWidth / 2, y + grid.rowHeight / 2);
        const targetIndex = targetDropId !== id ? items.length : getTargetIndex(i, grid, items.length, (isRtl ? -1 : 1) * state.movement[0], state.movement[1]);

        // traverse?
        if (traverse) {
          onChange(traverse.sourceId, traverse.sourceIndex, traverse.targetIndex, traverse.targetId);
        } else {
          onChange(id, i, targetIndex);
        }
        setPlaceholder(null);
        setDraggingIndex(null);
      }
      function onStart() {
        measureAll();
      }
      return <GridItemContext.Provider value={{
        top: pos.xy[1],
        disableDrag,
        endTraverse,
        mountWithTraverseTarget: isTraverseTarget ? [traverse.tx, traverse.ty] : undefined,
        left: pos.xy[0],
        i: nextIndex,
        onMove,
        onEnd,
        onStart,
        grid,
        dragging: i === draggingIndex
      }}>
                {child}
              </GridItemContext.Provider>;
    })}
    </animated.div>;
}